import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent } from "@bcc/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@bcc/components/ui/dropdown-menu";
import { Progress } from "@bcc/components/ui/progress";
import { Minus, MoreHorizontal, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  setInventoryTableHeaderTestId,
  setInventoryTableRowTestId,
  setInventoryTableStatusBadgeTestId,
  setInventoryTableTestId,
} from "./inventory-table.utils";

interface InventoryItem {
  id: number;
  product: string;
  sku: string;
  current: number;
  minimum: number;
  maximum: number;
  location: string;
  status: "in-stock" | "low-stock" | "out-of-stock";
}

interface InventoryTableProps {
  data: InventoryItem[];
  testId?: string;
}

const statusConfig = {
  "in-stock": {
    labelKey: "inventory.table.status.inStock",
    className: "bg-primary/10 text-primary hover:bg-primary/20",
  },
  "low-stock": {
    labelKey: "inventory.table.status.lowStock",
    className: "bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-950 dark:text-amber-400",
  },
  "out-of-stock": {
    labelKey: "inventory.table.status.outOfStock",
    className: "bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-950 dark:text-red-400",
  },
};

interface ProductCellProps {
  product: string;
}

function ProductCell({ product }: ProductCellProps) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-xl">☕</div>
      <p className="font-medium">{product}</p>
    </div>
  );
}

interface StockLevelProps {
  current: number;
  maximum: number;
}

function StockLevel({ current, maximum }: StockLevelProps) {
  const { t } = useTranslation();
  const stockPercentage = (current / maximum) * 100;

  return (
    <div className="space-y-2 min-w-[200px]">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">
          {current} / {maximum} {t("inventory.table.bags")}
        </span>
        <span className="text-muted-foreground">{Math.round(stockPercentage)}%</span>
      </div>
      <Progress value={stockPercentage} className="h-2" />
    </div>
  );
}

interface InventoryActionsProps {
  onAdd: () => void;
  onRemove: () => void;
  onViewHistory: () => void;
  onAdjustStock: () => void;
  onReorder: () => void;
}

function InventoryActions({ onAdd, onRemove, onViewHistory, onAdjustStock, onReorder }: InventoryActionsProps) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-end gap-2">
      <Button variant="outline" size="icon" className="h-8 w-8 bg-transparent" onClick={onAdd}>
        <Plus className="h-4 w-4" />
      </Button>
      <Button variant="outline" size="icon" className="h-8 w-8 bg-transparent" onClick={onRemove}>
        <Minus className="h-4 w-4" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">{t("inventory.table.openMenu")}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onViewHistory}>{t("inventory.table.actions.viewHistory")}</DropdownMenuItem>
          <DropdownMenuItem onClick={onAdjustStock}>{t("inventory.table.actions.adjustStock")}</DropdownMenuItem>
          <DropdownMenuItem onClick={onReorder}>{t("inventory.table.actions.reorder")}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

interface InventoryRowProps {
  item: InventoryItem;
}

function InventoryRow({ item }: InventoryRowProps) {
  const { t } = useTranslation();

  const handleAdd = () => {
    console.log("Add stock for", item.id);
  };

  const handleRemove = () => {
    console.log("Remove stock for", item.id);
  };

  const handleViewHistory = () => {
    console.log("View history for", item.id);
  };

  const handleAdjustStock = () => {
    console.log("Adjust stock for", item.id);
  };

  const handleReorder = () => {
    console.log("Reorder", item.id);
  };

  return (
    <tr className="hover:bg-muted/50 transition-colors" data-testid={setInventoryTableRowTestId(item.id)}>
      <td className="px-6 py-4" data-testid={setInventoryTableTestId(`product-${item.id}`)}>
        <ProductCell product={item.product} />
      </td>
      <td className="px-6 py-4" data-testid={setInventoryTableTestId(`sku-${item.id}`)}>
        <span className="font-mono text-sm text-muted-foreground">{item.sku}</span>
      </td>
      <td className="px-6 py-4">
        <StockLevel current={item.current} maximum={item.maximum} />
      </td>
      <td className="px-6 py-4">
        <span className="text-sm text-muted-foreground">{item.location}</span>
      </td>
      <td className="px-6 py-4">
        <Badge
          variant="secondary"
          className={statusConfig[item.status].className}
          data-testid={setInventoryTableStatusBadgeTestId(item.status)}
        >
          {t(statusConfig[item.status].labelKey as string) as string}
        </Badge>
      </td>
      <td className="px-6 py-4 text-right">
        <InventoryActions
          onAdd={handleAdd}
          onRemove={handleRemove}
          onViewHistory={handleViewHistory}
          onAdjustStock={handleAdjustStock}
          onReorder={handleReorder}
        />
      </td>
    </tr>
  );
}

export function InventoryTable({ data, testId }: InventoryTableProps) {
  const { t } = useTranslation();

  return (
    <Card testId={testId ?? setInventoryTableTestId("container")}>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold" data-testid={setInventoryTableHeaderTestId("product")}>
                  {t("inventory.table.headers.product")}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold" data-testid={setInventoryTableHeaderTestId("sku")}>
                  {t("inventory.table.headers.sku")}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold" data-testid={setInventoryTableHeaderTestId("stock-level")}>
                  {t("inventory.table.headers.stockLevel")}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold" data-testid={setInventoryTableHeaderTestId("location")}>
                  {t("inventory.table.headers.location")}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold" data-testid={setInventoryTableHeaderTestId("status")}>
                  {t("inventory.table.headers.status")}
                </th>
                <th className="px-6 py-4 text-right text-sm font-semibold" data-testid={setInventoryTableHeaderTestId("actions")}>
                  {t("inventory.table.headers.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((item) => (
                <InventoryRow key={item.id} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
